import { PRIMARIES_WITHOUT_SUFFIX, S3 } from "../../../constants";
import i18n from "../../../i18n";
import { METCON_TYPES } from "../../../utils/metcon";

/**
 * Get experience level bar filled width
 * @param  {String} difficulty Program's difficulty
 * @return {Number} Width amount
 */
export const getExperienceBarWidth = (difficulty) => {
  switch (difficulty) {
    case "Beginner":
      return 10;
    case "Intermediate":
      return 50;
    case "Expert":
      return 100;
    default:
      return 50;
  }
};

/**
 * Get exercise name to display
 * @param  {Object} exercise Exercise to get name of
 * @return {String} exercise name to display
 */
export const getExerciseName = (exercise) => {
  if (exercise.complexName) return exercise.complexName;
  if (exercise.primary === "metcon" && exercise.subType) {
    let metconName = METCON_TYPES.find((mt) => mt.value === exercise.subType)
      ? i18n.t(`coaching-portal:addMetcon.metconTypes.${exercise.subType}`)
      : exercise.subType.toUpperCase();
    if (exercise.customName) {
      metconName += ` - ${exercise.customName}`;
    }
    if (exercise.subType === "custom" && exercise.customName)
      return exercise.customName;
    return metconName;
  }
  let exerciseName;
  const { secondary, type } = exercise;
  let primary = exercise.primary;
  if (primary === "overheadPress") primary = "Overhead Press";

  if (type === "primary") {
    exerciseName = `${primary}`;
  } else if (
    type === "secondary" &&
    !PRIMARIES_WITHOUT_SUFFIX.includes(primary)
  ) {
    exerciseName = `${secondary || ""} (${primary})`;
  } else {
    exerciseName = `${secondary}`;
  }

  return exerciseName.charAt(0).toUpperCase() + exerciseName.slice(1);
};

/**
 * Get unique exercises for each block in a program
 * @param  {Object} exercises List of a program's blocks with all exercises
 * @return {Object} List of a program's blocks with unique exercises
 */
export const getUniqueExercises = (exercises) => {
  let newExercises = {};
  for (const key in exercises) {
    newExercises[key] = exercises[key].map((exercise) =>
      getExerciseName(exercise).trim()
    );
    // this will replace the array with a new array of unique names
    newExercises[key] = [...new Set(newExercises[key])];
  }
  return newExercises;
};

/**
 * Get the program creator name (dynamic depending on program type)
 * @param  {Object} program Program to get creator's name of
 * @return {String} Program creator's name
 */
export const getProgramCreatorName = (program) => {
  let creatorName;

  if (program.type === "subscription") {
    creatorName = `Team ${
      program.createdBy.businessName || program.createdBy.name
    }`;
  } else {
    creatorName = program.createdBy.name;
  }

  return creatorName;
};

/**
 * Get the path for a program's image
 * @param  {Object} program Program to get the image of
 * @return {String} Path to the program's image or placeholder image
 */
export const getProgramImagePath = (program) => {
  // if a program has an uploaded image, return a path to that in s3
  if (program && program.image && program.image.file) {
    const environment = process.env.NODE_ENV
      ? process.env.NODE_ENV
      : "production";
    return `${S3.PATH}appfiles/${environment}/images/${program.image.file}`;
  } else {
    // if it doesn't have an image, return placeholder image
    return "/img/msb-logo-notext.webp";
  }
};

/**
 * Get the path to program preview
 * @param  {String} programId id of the program to preview
 * @param  {Boolean} fromAthlete user is on athlete side of the app
 * @param  {Object} athlete athlete object from locals in case user is coach
 * @return {String} Path to the program preview page
 */
export const getProgramPreviewPath = (
  programId,
  fromAthlete = false,
  athlete = null
) => {
  let previewPath = fromAthlete
    ? `/dashboard/program-library/program-preview/${programId}?&program=1`
    : `/coaching-portal/templates/program-preview/${programId}?program=1`;

  if (athlete) {
    previewPath = `/athlete-portal/programs/program-preview/${programId}?program=1&athlete=${athlete._id}`;
  }

  return previewPath;
};
